import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
  Navigate,
  useNavigate,
} from "react-router-dom";
import LoginScreen from "../screens/LoginScreen";
import AdminUserManagementScreen from "../screens/AdminUserManagementScreen";
import AdminActionManagementScreen from "../screens/AdminActionManagementScreen";
import AdminContactsManagementScreen from "../screens/AdminContactsManagementScreen";
import DriverLogScreen from "../screens/DriverLogScreen";
import DriverLogHistoryScreen from "../screens/DriverLogHistoryScreen";
import EquipmentScreen from "../screens/EquipmentScreen";
import AddEquipmentScreen from "../screens/AddEquipmentScreen";
import OrderListScreen from "../screens/OrderListScreen";
import CreateOrderScreen from "../screens/CreateOrderScreen";
import OrderDetailScreen from "../screens/OrderDetailScreen";
import AdminJobsManagementScreen from "../screens/AdminJobsManagementScreen.js";
import CreateManualInvoiceScreen from "../screens/CreateManualInvoiceScreen.js";
import AdminInvoiceManagementScreen from "../screens/AdminInvoiceManagementScreen.js";
import AdminEmailManagementScreen from "../screens/AdminEmailManagementScreen.js";
import { useAuth } from "../context/AuthContext";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import { ArrowLeftOnRectangleIcon } from "@heroicons/react/24/outline";

const styles = {
  nav: {
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    padding: "12px 24px",
    borderRadius: 20,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
    border: `1px solid ${COLORS.tertiary}`,
    position: "fixed",
    top: 12,
    left: 0,
    right: 0,
    zIndex: 1000,
    margin: "0 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    animation: "fadeIn 0.5s ease-in-out",
  },
  navListContainer: {
    display: "flex",
    justifyContent: "center",
    flex: 1,
  },
  navList: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "center",
    listStyle: "none",
    justifyContent: "center",
  },
  navItem: {
    color: COLORS.text,
    textDecoration: "none",
    padding: "8px 16px",
    borderRadius: 8,
    fontFamily: FONTS.regularInter,
    fontSize: 16,
    fontWeight: 500,
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: COLORS.primary,
      color: COLORS.white,
      transform: "scale(1.05)",
    },
  },
  navItemActive: {
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    fontWeight: 600,
  },
  signOutButton: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 16px",
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    borderRadius: 12,
    fontFamily: FONTS.bold,
    fontSize: 16,
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
    "&:hover": {
      backgroundColor: COLORS.white,
      color: COLORS.primary,
      transform: "scale(1.05)",
      boxShadow: `0 6px 16px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.3)`,
    },
  },
  signOutIcon: {
    width: 20,
    height: 20,
  },
  content: {
    minHeight: "100vh",
    backgroundColor: COLORS.white,
  },
  contentWithNav: {
    paddingTop: 104, // Clear the fixed header so page content isn't covered
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(-10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@media (max-width: 768px)": {
    nav: {
      margin: "0 12px",
      padding: "12px 16px",
      flexDirection: "column",
      gap: 12,
    },
    navListContainer: {
      width: "100%",
    },
    navList: {
      flexDirection: "column",
      alignItems: "stretch",
    },
    navItem: {
      padding: "10px 12px",
      textAlign: "center",
    },
    signOutButton: {
      padding: "10px 12px",
      width: "100%",
      justifyContent: "center",
    },
    contentWithNav: {
      paddingTop: 168, // Stacked nav is taller on mobile; keep content clear
    },
  },
};

const AdminRedirect = () => {
  const navigate = useNavigate();
  const { user, userType } = useAuth();

  useEffect(() => {
    if (user && userType === "admin") {
      navigate("/admin/users", { replace: true });
    }
  }, [user, userType, navigate]);

  return null;
};

const UserRedirect = () => {
  const navigate = useNavigate();
  const { user, userType } = useAuth();

  useEffect(() => {
    if (user && userType !== "admin") {
      if (userType === "driver" || userType === "both") {
        navigate("/user/log", { replace: true });
      } else if (userType === "equipmentMan") {
        navigate("/create-order", { replace: true });
      }
    }
  }, [user, userType, navigate]);

  return null;
};

const AppNavigator = () => {
  const { user, userType, signOut, loading } = useAuth();

  // While Firebase is restoring the session, don't render <Routes> yet. The
  // route table depends on `user`, so rendering during this window would leave
  // a deep URL (e.g. /admin/invoices reached via reload or browser Back) with
  // no matching route -> blank white page.
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: COLORS.white,
          color: COLORS.text,
          fontFamily: FONTS.regularInter,
          fontSize: 18,
        }}
      >
        Loading...
      </div>
    );
  }

  const handleSignOut = async () => {
    const success = await signOut();
    if (success) {
      window.location.href = "/";
    } else {
      alert("Failed to sign out. Please try again.");
    }
  };

  return (
    <Router>
      <div
        style={
          user
            ? { ...styles.content, ...styles.contentWithNav }
            : styles.content
        }
      >
        {user && (
          <nav style={styles.nav}>
            <div style={styles.navListContainer}>
              <ul style={styles.navList}>
                {userType === "admin" ? (
                  <>
                    <li>
                      <NavLink
                        to="/admin/users"
                        style={({ isActive }) => ({
                          ...styles.navItem,
                          ...(isActive ? styles.navItemActive : {}),
                        })}
                      >
                        User Management
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/admin/jobs"
                        style={({ isActive }) => ({
                          ...styles.navItem,
                          ...(isActive ? styles.navItemActive : {}),
                        })}
                      >
                        Jobs Management
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/admin/actions"
                        style={({ isActive }) => ({
                          ...styles.navItem,
                          ...(isActive ? styles.navItemActive : {}),
                        })}
                      >
                        Actions Management
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/admin/contacts"
                        style={({ isActive }) => ({
                          ...styles.navItem,
                          ...(isActive ? styles.navItemActive : {}),
                        })}
                      >
                        Contacts Management
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/admin/equipment"
                        style={({ isActive }) => ({
                          ...styles.navItem,
                          ...(isActive ? styles.navItemActive : {}),
                        })}
                      >
                        Equipment
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/admin/invoices"
                        style={({ isActive }) => ({
                          ...styles.navItem,
                          ...(isActive ? styles.navItemActive : {}),
                        })}
                      >
                        Invoices
                      </NavLink>
                    </li>
                    <li>
                      <NavLink
                        to="/admin/emails"
                        style={({ isActive }) => ({
                          ...styles.navItem,
                          ...(isActive ? styles.navItemActive : {}),
                        })}
                      >
                        Email Recipients
                      </NavLink>
                    </li>
                  </>
                ) : (
                  <>
                    {(userType === "driver" || userType === "both") && (
                      <>
                        <li>
                          <NavLink
                            to="/user/log"
                            style={({ isActive }) => ({
                              ...styles.navItem,
                              ...(isActive ? styles.navItemActive : {}),
                            })}
                          >
                            Driver Log
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/user/log-history"
                            style={({ isActive }) => ({
                              ...styles.navItem,
                              ...(isActive ? styles.navItemActive : {}),
                            })}
                          >
                            Log History
                          </NavLink>
                        </li>
                      </>
                    )}
                    {(userType === "equipmentMan" || userType === "both") && (
                      <>
                        <li>
                          <NavLink
                            to="/create-order"
                            style={({ isActive }) => ({
                              ...styles.navItem,
                              ...(isActive ? styles.navItemActive : {}),
                            })}
                          >
                            Orders
                          </NavLink>
                        </li>
                        <li>
                          <NavLink
                            to="/create-manual-invoice"
                            style={({ isActive }) => ({
                              ...styles.navItem,
                              ...(isActive ? styles.navItemActive : {}),
                            })}
                          >
                            Invoice
                          </NavLink>
                        </li>
                      </>
                    )}
                  </>
                )}
              </ul>
            </div>
            <button style={styles.signOutButton} onClick={handleSignOut}>
              <ArrowLeftOnRectangleIcon style={styles.signOutIcon} />
              <span>Sign Out</span>
            </button>
          </nav>
        )}
        <Routes>
          {!user ? (
            <>
              <Route path="/" element={<LoginScreen />} />
              {/* Any deep URL while logged out -> send to login, never blank. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : userType === "admin" ? (
            <>
              <Route
                path="/admin/users"
                element={<AdminUserManagementScreen />}
              />
              <Route
                path="/admin/jobs"
                element={<AdminJobsManagementScreen />}
              />
              <Route
                path="/admin/actions"
                element={<AdminActionManagementScreen />}
              />
              <Route
                path="/admin/contacts"
                element={<AdminContactsManagementScreen />}
              />
              <Route path="/admin/equipment" element={<EquipmentScreen />} />
              <Route
                path="/admin/equipment/add"
                element={<AddEquipmentScreen />}
              />
              <Route
                path="/admin/invoices"
                element={<AdminInvoiceManagementScreen />}
              />
              <Route
                path="/admin/emails"
                element={<AdminEmailManagementScreen />}
              />
              <Route path="/admin" element={<AdminRedirect />} />
              <Route path="/" element={<AdminRedirect />} />
              {/* Unknown URL (e.g. a user-only route, or reload at a stale
                  path) -> bounce to the admin landing instead of blank. */}
              <Route path="*" element={<Navigate to="/admin/users" replace />} />
            </>
          ) : (
            <>
              {(userType === "driver" || userType === "both") && (
                <>
                  <Route path="/user/log" element={<DriverLogScreen />} />
                  <Route
                    path="/user/log-history"
                    element={<DriverLogHistoryScreen />}
                  />
                </>
              )}
              {(userType === "equipmentMan" || userType === "both") && (
                <>
                  <Route path="/create-order" element={<OrderListScreen />} />
                  <Route
                    path="/create-order/new"
                    element={<CreateOrderScreen />}
                  />
                  <Route
                    path="/create-order/edit"
                    element={<CreateOrderScreen />}
                  />
                  <Route
                    path="/order-detail/:id"
                    element={<OrderDetailScreen />}
                  />
                  <Route
                    path="/create-manual-invoice"
                    element={<CreateManualInvoiceScreen />}
                  />
                </>
              )}
              <Route path="/user" element={<UserRedirect />} />
              <Route path="/" element={<UserRedirect />} />
              {/* Unknown URL for a non-admin user -> bounce home, never blank. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </div>
    </Router>
  );
};

export default AppNavigator;
